import React, { useState, useEffect } from 'react';
import { apiFetch, getApiBaseUrl } from '../../utils/apiConfig';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  FormControlLabel,
  Typography,
  Alert,
  Chip,
  Tooltip,
  Divider,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  PolylineOutlined as PolylineOutlinedIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TenantMultiSelect from '../common/TenantMultiSelect';


const DicomSendNodes = () => {
  const { t } = useTranslation();
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: null
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    ae_title: '',
    ip_address: '',
    port: '',
    is_active: true,
    tenants: ''
  });

  useEffect(() => {
    fetchNodes();
  }, []);

  const fetchNodes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/dicom-send-nodes/`);
      if (response.ok) {
        const data = await response.json();
        setNodes(data);
      } else {
        throw new Error('Failed to fetch DICOM send nodes');
      }
    } catch (error) {
      console.error('Error fetching DICOM send nodes:', error);
      setMessage({ 
        type: 'error', 
        text: t('dicomSendNodes.snackbar.loadError')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectNode = (nodeId) => {
    setSelectedNodes(prev => 
      prev.includes(nodeId) 
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedNodes.length === nodes.length) {
      setSelectedNodes([]);
    } else {
      setSelectedNodes(nodes.map(node => node.id));
    }
  };

  const handleEditSelected = () => {
    if (selectedNodes.length === 1) {
      const node = nodes.find(n => n.id === selectedNodes[0]);
      handleOpenDialog(node);
    }
  };

  const handleTestSelected = () => {
    if (selectedNodes.length === 1) {
      handleTestConnection(selectedNodes[0]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedNodes.length === 0) return;
    
    let description;
    if (selectedNodes.length === 1) {
      const selectedNode = nodes.find(node => node.id === selectedNodes[0]);
      description = t('dicomSendNodes.confirmations.deleteNodeDescription_one', { name: selectedNode?.name || 'Unknown' });
    } else {
      description = t('dicomSendNodes.confirmations.deleteNodeDescription_other', { count: selectedNodes.length });
    }

    setConfirmDelete({
      open: true,
      title: t('dicomSendNodes.confirmations.deleteNodeTitle'),
      description: description,
      onConfirm: async () => {
        try {
          const deletePromises = selectedNodes.map(nodeId =>
            fetch(`${getApiBaseUrl()}/dicom-send-nodes/${nodeId}`, {
              method: 'DELETE'
            })
          );

          const results = await Promise.all(deletePromises);
          const allSuccessful = results.every(response => response.ok);

          if (allSuccessful) {
            setMessage({
              type: 'success',
              text: selectedNodes.length === 1 
                ? t('dicomSendNodes.deleteSuccess')
                : `${selectedNodes.length} DICOM-Sendeknoten erfolgreich gelöscht`
            });
            setSelectedNodes([]);
            fetchNodes();
          } else {
            throw new Error('Some deletions failed');
          }
        } catch (error) {
          console.error('Error deleting DICOM send nodes:', error);
          setMessage({
            type: 'error',
            text: t('dicomSendNodes.deleteError')
          });
        }
      }
    });
  };

  const handleOpenDialog = (node = null) => {
    if (node) {
      setEditingNode(node);
      setFormData({
        name: node.name,
        ae_title: node.ae_title,
        ip_address: node.ip_address,
        port: node.port.toString(),
        is_active: node.is_active,
        tenants: node.tenants || ''
      });
    } else {
      setEditingNode(null);
      setFormData({
        name: '',
        ae_title: '',
        ip_address: '',
        port: '',
        is_active: true,
        tenants: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNode(null);
    setFormData({
      name: '',
      ae_title: '',
      ip_address: '',
      port: '',
      is_active: true,
      tenants: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      const url = editingNode 
        ? `${getApiBaseUrl()}/dicom-send-nodes/${editingNode.id}`
        : `${getApiBaseUrl()}/dicom-send-nodes/`;
      
      const method = editingNode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          port: parseInt(formData.port)
        })
      });

      if (response.ok) {
        setMessage({
          type: 'success',
          text: editingNode 
            ? 'DICOM-Sendeknoten erfolgreich aktualisiert'
            : 'DICOM-Sendeknoten erfolgreich erstellt'
        });
        handleCloseDialog();
        fetchNodes();
      } else {
        throw new Error('Failed to save DICOM send node');
      }
    } catch (error) {
      console.error('Error saving DICOM send node:', error);
      setMessage({
        type: 'error',
        text: 'Fehler beim Speichern des DICOM-Sendeknotens'
      });
    }
  };

  const handleTestConnection = async (nodeId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/dicom-send-nodes/${nodeId}/test`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        setTestResults(prev => ({
          ...prev,
          [nodeId]: result
        }));
      } else {
        throw new Error('Failed to test DICOM connection');
      }
    } catch (error) {
      console.error('Error testing DICOM connection:', error);
      setTestResults(prev => ({
        ...prev,
        [nodeId]: {
          success: false,
          message: t('dicomSendNodes.testError'),
          details: error.message
        }
      }));
    }
  };



  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PolylineOutlinedIcon color="primary" />
        {t('admin.serverManagement.dicomSettings')}
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {/* Action Buttons */}
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mr: 2 }}>
            {selectedNodes.length > 0 
              ? t('dicomSendNodes.selectedNodes', { count: selectedNodes.length })
              : t('dicomSendNodes.noNodesSelected')
            }
          </Typography>
          <Divider orientation="vertical" flexItem />
          <Tooltip title={t('dicomSendNodes.edit')}>
            <IconButton 
              size="small" 
              onClick={handleEditSelected}
              disabled={loading || selectedNodes.length !== 1}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('dicomSendNodes.testConnection')}>
            <IconButton 
              size="small" 
              onClick={handleTestSelected}
              disabled={loading || selectedNodes.length !== 1}
            >
              <TestIcon />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          <Tooltip title={t('dicomSendNodes.delete')}>
            <IconButton 
              size="small" 
              onClick={handleDeleteSelected}
              disabled={loading || selectedNodes.length === 0}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ ml: 2, mr: 1 }} />
          <Box display="flex" alignItems="center" gap={1} flexGrow={1} justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              className="button"
            >
              {t('dicomSendNodes.addNode')}
            </Button>
          </Box>
        </Box>

        <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedNodes.length === nodes.length && nodes.length > 0}
                  indeterminate={selectedNodes.length > 0 && selectedNodes.length < nodes.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>{t('dicomSendNodes.name')}</TableCell>
              <TableCell>{t('dicomSendNodes.aeTitle')}</TableCell>
              <TableCell>{t('dicomSendNodes.ipAddress')}</TableCell>
              <TableCell>{t('dicomSendNodes.port')}</TableCell>
              <TableCell>{t('dicomSendNodes.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nodes.map((node) => (
              <TableRow key={node.id}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedNodes.includes(node.id)}
                    onChange={() => handleSelectNode(node.id)}
                  />
                </TableCell>
                <TableCell>{node.name}</TableCell>
                <TableCell>{node.ae_title}</TableCell>
                <TableCell>{node.ip_address}</TableCell>
                <TableCell>{node.port}</TableCell>
                <TableCell>
                  <Chip
                    label={node.is_active ? t('dicomSendNodes.active') : t('dicomSendNodes.inactive')}
                    color={node.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Test Results Display */}
        {Object.keys(testResults).length > 0 && (
          <Box mt={2}>
            <Typography variant="h6" gutterBottom>
              {t('dicomSendNodes.testResults')}
            </Typography>
            {Object.entries(testResults).map(([nodeId, result]) => {
              const node = nodes.find(n => n.id === parseInt(nodeId));
              return (
                <Alert
                  key={nodeId}
                  severity={result.success ? 'success' : 'error'}
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2">
                    {node?.name} ({node?.ae_title}): {result.message}
                  </Typography>
                  {result.details && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {t('dicomSendNodes.details')}: {result.details}
                    </Typography>
                  )}
                </Alert>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingNode ? t('dicomSendNodes.editNode') : t('dicomSendNodes.addNode')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('dicomSendNodes.name')}
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={t('dicomSendNodes.aeTitle')}
            value={formData.ae_title}
            onChange={(e) => handleInputChange('ae_title', e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={t('dicomSendNodes.ipAddress')}
            value={formData.ip_address}
            onChange={(e) => handleInputChange('ip_address', e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={t('dicomSendNodes.port')}
            type="number"
            value={formData.port}
            onChange={(e) => handleInputChange('port', e.target.value)}
            margin="normal"
            required
            inputProps={{ min: 1, max: 65535 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
              />
            }
            label={t('dicomSendNodes.active')}
            sx={{ mt: 2, mb: 2 }}
          />
          <TenantMultiSelect
            value={formData.tenants}
            onChange={(value) => handleInputChange('tenants', value)}
            label={t('dicomSendNodes.tenants', 'Verfügbare Tenants')}
            helperText={t('dicomSendNodes.tenantsHelp', 'Leer = für alle Tenants verfügbar')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} className="button-cancel">
            {t('dicomSendNodes.cancel')}
          </Button>
          <Button onClick={handleSubmit} variant="contained" className="button">
            {editingNode ? t('dicomSendNodes.update') : t('dicomSendNodes.add')}
          </Button>
        </DialogActions>
      </Dialog>



      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ ...confirmDelete, open: false })}
      >
        <DialogTitle>{confirmDelete.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDelete.description}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ ...confirmDelete, open: false })} className="button-cancel">
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={() => {
              if (confirmDelete.onConfirm) confirmDelete.onConfirm();
              setConfirmDelete({ ...confirmDelete, open: false });
            }} 
            color="primary" 
            autoFocus
            className="button"
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DicomSendNodes; 